<template>
    <div>
        <v-dialog v-model="internalDialog" max-width="960" scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <span class="text-h6">{{ unit }} - Remote Team</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="close">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-autocomplete v-model="selectedNewUserId" :items="availableUsers" item-text="displayLabel"
                        item-value="id" label="Add a remote user" prepend-inner-icon="mdi-account-plus" outlined
                        dense clearable @change="onSelectNewUser">
                        <template v-slot:item="{ item }">
                            <v-list-item-avatar size="32">
                                <v-img v-if="item.profile_picture" :src="getProfilePictureUrl(item.profile_picture)"></v-img>
                                <v-avatar v-else color="primary" size="32" class="white--text">{{ initials(item) }}</v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ roleLabel(item) }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>

                    <v-row v-if="remoteUsers.length" dense class="mt-2">
                        <v-col v-for="ru in remoteUsers" :key="ru.user.id" cols="12" sm="4">
                            <v-card outlined class="pa-3 remote-user-card" @click="viewUser(ru.user)">
                                <div class="d-flex align-center mb-2">
                                    <v-avatar size="56" class="mr-3">
                                        <v-img v-if="ru.user.profile_picture" :src="getProfilePictureUrl(ru.user.profile_picture)"></v-img>
                                        <v-avatar v-else color="primary" size="56" class="white--text">{{ initials(ru.user) }}</v-avatar>
                                    </v-avatar>
                                    <div class="overflow-hidden">
                                        <div class="text-body-2 font-weight-bold text-truncate">{{ ru.user.name }}</div>
                                        <div v-if="detailTitle(ru.user)" class="text-caption grey--text text-truncate">{{ detailTitle(ru.user) }}</div>
                                    </div>
                                </div>
                                <div v-if="compactFields(ru.user).length" class="remote-user-details">
                                    <div v-for="field in compactFields(ru.user)" :key="field.key" class="text-caption">
                                        <strong>{{ field.label }}:</strong> {{ field.value }}
                                    </div>
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>
                    <div v-else class="text-center text--secondary py-6">No remote users added yet.</div>
                </v-card-text>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="close">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <UserDetailsDialog v-model="showViewDialog" :user="viewingUser">
            <template v-slot:extra-info="{ user }">
                <div v-if="detailFieldsFor(user).length" class="mb-6">
                    <h4 class="text-subtitle-1 font-weight-bold mb-3">Get to Know {{ firstNameOf(user) }}</h4>
                    <v-row dense>
                        <v-col v-for="field in detailFieldsFor(user)" :key="field.key" cols="12" sm="6">
                            <div class="text-caption font-weight-bold grey--text text--darken-1">{{ field.label }}</div>
                            <div class="text-body-2 mb-2">{{ field.value }}</div>
                        </v-col>
                    </v-row>
                </div>
            </template>
            <template v-slot:actions="{ user, close: closeView }">
                <v-btn text color="error" :loading="removing" @click="removeUser(user)">Remove from Unit</v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeView">Close</v-btn>
                <v-btn v-if="isViewingOwnDetails" color="primary" text @click="editUser(user, closeView)">Edit Details</v-btn>
            </template>
        </UserDetailsDialog>

        <FloorPlanUserDetailsDialog v-model="showFormDialog" :user="formUser"
            :details="formUser ? floorPlanUserDetails()[formUser.id] : null" :saving="savingForm"
            @save="handleSaveDetails" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserDetailsDialog from '../../modules/UserDetailsDialog.vue';
import FloorPlanUserDetailsDialog from './FloorPlanUserDetailsDialog.vue';
import { firstName, detailFields } from '../userDetailFields';
import { NO_TEAM_UNIT } from '../unitColors';

export default {
    name: 'UnitRemoteUsersDialog',
    components: { UserDetailsDialog, FloorPlanUserDetailsDialog },
    inject: ['users', 'floorPlanRemoteUsers', 'addFloorPlanRemoteUser', 'removeFloorPlanRemoteUser', 'floorPlanUserDetails', 'refreshFloorPlanUserDetails', 'saveFloorPlanUserDetails'],
    props: {
        value: { type: Boolean, default: false },
        unit: { type: String, default: '' },
    },
    data() {
        return {
            internalDialog: this.value,
            selectedNewUserId: null,
            showViewDialog: false,
            viewingUser: null,
            showFormDialog: false,
            formUser: null,
            savingForm: false,
            removing: false,
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        remoteUsers() {
            return (this.floorPlanRemoteUsers() || []).filter((ru) => ru.unit === this.unit && ru.user);
        },
        availableUsers() {
            const existingIds = new Set(this.remoteUsers.map((ru) => ru.user.id));
            return (this.users() || [])
                .filter((user) => !existingIds.has(user.id))
                .map((user) => ({ ...user, displayLabel: `${user.name} (${this.roleLabel(user)})` }));
        },
        isViewingOwnDetails() {
            return !!(this.viewingUser && this.get_user && this.viewingUser.id === this.get_user.id);
        },
    },
    watch: {
        value(val) {
            this.internalDialog = val;
            if (val) this.loadMissingDetails();
        },
        internalDialog(val) {
            this.$emit('input', val);
        },
    },
    methods: {
        loadMissingDetails() {
            const existing = this.floorPlanUserDetails();
            this.remoteUsers.forEach((ru) => {
                if (!(ru.user.id in existing)) {
                    this.refreshFloorPlanUserDetails(ru.user.id).catch((error) => {
                        console.error('Error fetching remote user details:', error);
                    });
                }
            });
        },
        close() {
            this.internalDialog = false;
        },
        roleLabel(user) {
            if (user.role_members && user.role_members.length) {
                return user.role_members.map((m) => m.role && m.role.name).filter(Boolean).join(', ') || 'No Role';
            }
            return (user.role && user.role.name) || 'No Role';
        },
        initials(user) {
            if (!user || !user.name) return '?';
            const names = user.name.split(' ');
            return names.length >= 2 ? (names[0][0] + names[1][0]).toUpperCase() : user.name.substring(0, 2).toUpperCase();
        },
        getProfilePictureUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${this.apiUrl}/${path}`;
        },
        detailTitle(user) {
            const details = this.floorPlanUserDetails()[user.id];
            return details && details.title;
        },
        compactFields(user) {
            const details = this.floorPlanUserDetails()[user.id];
            return detailFields(details, ['start_date', 'from_location', 'company_email', 'what_i_actually_do', 'hobbies', 'ask_me_about', 'fun_fact']);
        },
        detailFieldsFor(user) {
            if (!user) return [];
            return detailFields(this.floorPlanUserDetails()[user.id]);
        },
        firstNameOf(user) {
            return firstName(user);
        },
        onSelectNewUser(userId) {
            if (!userId) return;
            const user = (this.users() || []).find((u) => u.id === userId);
            if (!user) return;
            this.selectedNewUserId = null;
            this.formUser = user;
            this.refreshFloorPlanUserDetails(user.id).catch((error) => {
                console.error('Error fetching existing user details:', error);
            }).finally(() => {
                this.showFormDialog = true;
            });
        },
        viewUser(user) {
            this.viewingUser = user;
            this.showViewDialog = true;
            this.refreshFloorPlanUserDetails(user.id).catch((error) => {
                console.error('Error fetching user details:', error);
            });
        },
        editUser(user, closeView) {
            closeView();
            this.formUser = user;
            this.showFormDialog = true;
        },
        async removeUser(user) {
            if (!user) return;
            this.removing = true;
            try {
                await this.removeFloorPlanRemoteUser(user.id);
                this.showViewDialog = false;
                this.$toast.success('Removed from unit');
            } catch (error) {
                console.error('Error removing remote user:', error);
                this.$toast.error('Failed to remove remote user');
            } finally {
                this.removing = false;
            }
        },
        async handleSaveDetails(formData) {
            if (!this.formUser) return;
            this.savingForm = true;
            try {
                await this.saveFloorPlanUserDetails(this.formUser.id, formData);
                const targetUnit = (formData.unit && formData.unit.trim()) || this.unit || NO_TEAM_UNIT;
                await this.addFloorPlanRemoteUser(targetUnit, this.formUser.id);
                this.showFormDialog = false;
                this.$toast.success('Remote user saved');
            } catch (error) {
                console.error('Error saving remote user:', error);
                this.$toast.error('Failed to save remote user');
            } finally {
                this.savingForm = false;
            }
        },
    },
};
</script>

<style scoped>
.remote-user-card {
    cursor: pointer;
    transition: box-shadow 0.15s ease;
    height: 100%;
}

.remote-user-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.remote-user-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    white-space: normal;
    word-break: break-word;
}
</style>
